import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Eye, Loader2, RefreshCw, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/admin/PageHeader';
import { WorkflowStepper } from '../../components/admin/WorkflowStepper';
import { Panel } from '../../components/Panel';
import { A4Frame } from '../../components/notice/A4Frame';
import { ScannedNotice } from '../../components/notice/ScannedNotice';
import { useDemo } from '../../contexts/DemoContext';
import { LOW_CONFIDENCE, ocrFields } from '../../data/sampleCase';
import type { CaseFieldKey } from '../../types/notice';
import { toBn } from '../../utils/bangla';
const steps = ['ডকুমেন্ট বিশ্লেষণ', 'লেখা শনাক্তকরণ', 'মামলার তথ্য সংগ্রহ', 'তথ্যের নির্ভুলতা মূল্যায়ন', 'প্রক্রিয়া সম্পন্ন'];
const shownScores = ['caseNo', 'vehicle', 'owner', 'mobile', 'offence', 'address', 'law'];
const LOW_KEYS: CaseFieldKey[] = ocrFields.filter(f => f.confidence < LOW_CONFIDENCE).map(f => f.key);
export function OcrProcessing() {
  const navigate = useNavigate();
  const {
    completeStage
  } = useDemo();
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);
  useEffect(() => {
    setStep(0);
    const t = setInterval(() => setStep(s => s >= steps.length ? s : s + 1), 950);
    return () => clearInterval(t);
  }, [run]);
  const done = step >= steps.length;
  useEffect(() => {
    if (done) completeStage(2);
  }, [done, completeStage]);
  const pct = Math.round(Math.min(step, steps.length) / steps.length * 100);
  const fields = ocrFields.filter(f => shownScores.includes(f.key));
  const lowCount = fields.filter(f => f.confidence < LOW_CONFIDENCE).length;
  return <div data-id="element-1319" data-mp="x3u0igxx0srm">
      <PageHeader screen="WEB SCREEN 05" title="AI/OCR তথ্য সংগ্রহ" subtitle="মামলা নং DMP/AI/2025/0614/0748 • প্রোটোটাইপে পূর্বনির্ধারিত নমুনা OCR ফলাফল ব্যবহৃত" data-id="element-1320" data-mp="x3kgrb3grhud" />
      <WorkflowStepper current={1} data-id="element-1321" data-mp="3ga6fpxr725j5" />
      <div className="grid gap-6 xl:grid-cols-[1fr_1fr]" data-id="element-1322" data-mp="3gb7sc6nuz02z">
        <Panel title="আপলোডকৃত নোটিশ" subtitle="DMP_Notice_0614_0748_scan.pdf" bodyClassName="bg-slate-100 p-4" data-id="element-1323" data-mp="3kbrnx5ss9vn0">
          <A4Frame data-id="element-1324" data-mp="36c8qp81ig4ph"><ScannedNotice scanning={!done} lowFields={done ? LOW_KEYS : []} data-id="element-1325" data-mp="36cia2cs1vdm7" /></A4Frame>
        </Panel>

        <div className="space-y-6" data-id="element-1326" data-mp="3baaci0qbmi4n">
          <Panel title="প্রক্রিয়াকরণ অগ্রগতি" actions={<span className="text-sm font-semibold text-[#003B71]" data-id="element-1328" data-mp="2wmfgobh0nxnc">{toBn(pct)}%</span>} data-id="element-1327" data-mp="3b9ii2t8gmqfy">
            <div className="mb-5 h-2.5 rounded-full bg-slate-100" data-id="element-1329" data-mp="31cvx84g4vzdx">
              <div className="h-2.5 rounded-full bg-[#1769AA] transition-all duration-700" style={{
              width: `${pct}%`
            }} data-id="element-1330" data-mp="31e6ynxm3wv1v" />
            </div>
            <ol className="space-y-3" data-id="element-1331" data-mp="2skc4tlqfqr1j">
              {steps.map((s, i) => {
              const state = i < step ? 'done' : i === step ? 'active' : 'todo';
              return <li key={s} className="flex items-center gap-3" data-id="element-1332" data-mp="2imehd0gegy9z" data-mpi={i}>
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full ${state === 'done' ? 'bg-[#16834A] text-white' : state === 'active' ? 'bg-[#E8F1FA] text-[#1769AA]' : 'bg-slate-100 text-slate-400'}`} data-id="element-1333" data-mp="2ilmlrpmsz0jp">
                      {state === 'done' ? <Check className="h-4 w-4" data-id="element-1334" data-mp="28zbkyf89ikyo" /> : state === 'active' ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1335" data-mp="2903jnyx5oyt6" /> : <span className="text-xs" data-id="element-1336" data-mp="23z61vng266s7">{toBn(i + 1)}</span>}
                    </span>
                    <span className={`text-sm ${state === 'todo' ? 'text-slate-400' : 'font-medium text-slate-800'}`} data-id="element-1337" data-mp="2hz8h11wzem3r">{s}</span>
                    {state === 'done' && <span className="ml-auto text-xs text-slate-400" data-id="element-1338" data-mp="2dxde5szek0nv">{toBn((0.4 + i * 0.3).toFixed(1))} সেকেন্ড</span>}
                  </li>;
            })}
            </ol>
          </Panel>

          <Panel title="ক্ষেত্রভিত্তিক নির্ভুলতা (Confidence Score)" data-id="element-1339" data-mp="2mqysjb7nnfwr">
            {!done ? <div className="py-8 text-center text-sm text-slate-500" data-id="element-1340" data-mp="31nplp2qqjvdx">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-[#1769AA]" data-id="element-1341" data-mp="2wpfo6lhjgip5" /> তথ্য সংগ্রহ চলছে…
              </div> : <>
                {lowCount > 0 && <div className="mb-4 flex items-start gap-2 rounded-md border border-[#E69A20]/40 bg-[#FDF3E2] p-3 text-sm text-[#8a5a06]" data-id="element-1342" data-mp="yzr1kx9u4tya">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1343" data-mp="13tv7dd7d6vxs" />
                    <span data-id="element-1344" data-mp="18q2h7h2vsrl0">{toBn(lowCount)}টি ক্ষেত্রের নির্ভুলতা {toBn(LOW_CONFIDENCE)}% এর কম। মূল নোটিশের সাথে মিলিয়ে ম্যানুয়াল যাচাই প্রয়োজন।</span>
                  </div>}
                <ul className="space-y-3" data-id="element-1345" data-mp="g2gdm8tnlmy5">
                  {fields.map(f => {
                const low = f.confidence < LOW_CONFIDENCE;
                return <li key={f.key} data-id="element-1346" data-mp="q15kz7nxbp05">
                        <div className="mb-1 flex justify-between text-sm" data-id="element-1347" data-mp="py18ln0b1mye">
                          <span className="flex items-center gap-1.5" data-id="element-1348" data-mp="1wfep8dmh2tlq">{f.label}{low && <AlertTriangle className="h-3.5 w-3.5 text-[#E69A20]" data-id="element-1349" data-mp="21erknmret8zx" />}</span>
                          <span className={`font-semibold ${low ? 'text-[#E69A20]' : 'text-[#16834A]'}`} data-id="element-1350" data-mp="3lx23fe8zsx13">{toBn(f.confidence)}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100" data-id="element-1351" data-mp="3gz0j6pztyro1">
                          <div className="h-2 rounded-full" style={{
                      width: `${f.confidence}%`,
                      backgroundColor: low ? '#E69A20' : '#16834A'
                    }} data-id="element-1352" data-mp="3c6gy77ytcu97" />
                        </div>
                      </li>;
              })}
                </ul>
              </>}
            <div className="mt-6 flex flex-wrap gap-2" data-id="element-1353" data-mp="2y6o89ev42i2a">
              <Button disabled={!done} onClick={() => navigate('/admin/review')} data-id="element-1354" data-mp="2ya3mkfgynn2n"><Eye className="h-4 w-4" data-id="element-1355" data-mp="2t9674anc574u" /> সংগৃহীত তথ্য দেখুন</Button>
              <Button variant="outline" onClick={() => setRun(r => r + 1)} data-id="element-1356" data-mp="2oacv6mnp5o6s"><RefreshCw className="h-4 w-4" data-id="element-1357" data-mp="2obw7y3a59h3t" /> পুনরায় OCR করুন</Button>
              <Button variant="outline" disabled={!done} onClick={() => navigate('/admin/review')} data-id="element-1358" data-mp="rgi1jun7y7lw"><UserCheck className="h-4 w-4" data-id="element-1359" data-mp="mjhuphijp0tq" /> ম্যানুয়াল যাচাই</Button>
            </div>
          </Panel>
        </div>
      </div>
    </div>;
}